import {
  useEffect,
  useRef,
  useState,
  type FC,
  type HTMLAttributes,
  type ImgHTMLAttributes,
} from "react";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { cn } from "lib/utils";
import { resolveSignedImageUrl } from "utils/signedImageUrl";

interface Props extends HTMLAttributes<HTMLDivElement> {
  imageUrl: string;
  alt?: string;
  imgClassName?: string;
  imgProps?: Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "alt">;
}

const REQUEST_TIMEOUT_MS = 15000;

export const Image: FC<Props> = ({
  imageUrl,
  alt = "Question asset",
  className,
  imgClassName,
  imgProps,
  ...rest
}) => {
  const [signedUrl, setSignedUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!imageUrl) {
      setLoading(false);
      setError("No image URL provided");
      setSignedUrl("");
      return;
    }

    let didCancel = false;
    setLoading(true);
    setError(null);
    setSignedUrl("");

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    timeoutRef.current = setTimeout(() => {
      if (!didCancel) {
        setError("Failed to load image: request timed out");
        setLoading(false);
      }
    }, REQUEST_TIMEOUT_MS);

    const resolveImage = async () => {
      try {
        const url = await resolveSignedImageUrl(imageUrl);

        if (!didCancel) {
          setSignedUrl(url);
          setLoading(false);
          setError(null);
        }
      } catch (err) {
        if (!didCancel) {
          const message = err instanceof Error ? err.message : "Unknown error";
          setError(message);
          setLoading(false);
        }
      } finally {
        if (!didCancel && timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    };

    resolveImage();

    return () => {
      didCancel = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [imageUrl, retryCount]);

  if (loading) {
    return (
      <div className="flex h-[200px] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !signedUrl) {
    return (
      <div className="flex h-[200px] flex-col items-center justify-center">
        <p className="mb-2 text-sm text-destructive">
          Failed to load image.
        </p>
        <Button
          variant="secondary"
          size="auto"
          onClick={() => {
            setRetryCount((count) => count + 1);
            setError(null);
            setLoading(true);
          }}
        >
          Retry
        </Button>
        {error && (
          <p className="mt-1 text-sm text-muted-foreground">
            {error}
          </p>
        )}
      </div>
    );
  }

  const { className: imgPropsClassName, ...restImgProps } = imgProps ?? {};

  return (
    <div className={cn("w-full overflow-hidden rounded-3xl", className)} {...rest}>
      <img
        src={signedUrl}
        alt={alt}
        className={cn("w-full h-full object-cover", imgPropsClassName, imgClassName)}
        {...restImgProps}
      />
    </div>
  );
};
